import { computed, ref, inject, provide, type EmitFn, type Ref, type ComputedRef } from 'vue'
import type {
  CollectionGroup,
  CollectionPlayer,
  CollectionSlide,
} from '@collection-player/types/collection-player.type'
import type Swiper from 'swiper'
import { type File } from '@openapi'
import useZoom from '@collection-player/composables/zoom.use'
import useNewZoom from '@collection-player/composables/new-zoom.use'
import useFullscreen from '@collection-player/composables/fullscreen.use'
import type { CollectionFileEntity, NodeInfo } from '@canvas-builder/types/canvas'
import type { ActionCallback } from '@collection-player/composables/keyboard-bindings.use'
import useKeyboardBindings from '@collection-player/composables/keyboard-bindings.use'
import useSlideDirection, { AxisDirection } from '@collection-player/composables/slide-direction.use'
import { useCacheNavigation } from '@collection-player/composables/navigation-cache.use'
import { useAnnotationLayer } from '@collection-player/composables/annotation-layer.use'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'

const StoreKey = Symbol('CollectionPlayerStore')

export type RenderPageAsImage = (payload: {
  fileId: File['id']
  documentId?: File['id']
  pageIndex: number
  params: { width: number } | { height: number }
}) => Promise<ArrayBuffer>

export type Api = {
  renderPageAsImage: RenderPageAsImage
  onFileEnter?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFileExit?: (node: NodeInfo, file: CollectionFileEntity) => void
  onFilePageEnter?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
  onFilePageExit?: (node: NodeInfo, file: CollectionFileEntity, pageIndex: number) => void
}

export type Options = { videoStartTime?: number }

export type Emit = {
  close: []
  click: [event: MouseEvent]
}

type StorePayload = {
  isLoading: Ref<boolean>
  inModal: Ref<boolean>
  isEditMode: Ref<boolean>
  data: Ref<CollectionPlayer>
  rootElement: Ref<HTMLElement | null>
  api: Api
  emit: EmitFn<Emit>
  options?: Options
  nodeId?: string
}

type StoreData = {
  isLoading: Ref<boolean>
  inModal: Ref<boolean>
  fitToWidth: Ref<boolean>
  isEditMode: Ref<boolean>
  isOpenTableOfContents: Ref<boolean>
  swiperElement: Ref<(HTMLElement & { swiper: Swiper }) | null>
  zoom: ReturnType<typeof useZoom | typeof useNewZoom>
  fullscreen: ReturnType<typeof useFullscreen>
  data: Ref<CollectionPlayer>
  visibleGroups: ComputedRef<CollectionGroup[]>
  hasManyGroups: ComputedRef<boolean>
  activeSlide: Ref<CollectionSlide | null>
  activeGroup: Ref<CollectionGroup | null>
  setActiveSlide: (group?: CollectionGroup, slide?: CollectionSlide) => void
  onSlideChange: (customEvent: CustomEvent<[swiper: Swiper]>) => void
  activatePreviousGroup: () => void
  activateNextGroup: () => void
  toggleTableOfContents: () => void
  toggleFitToWidth: () => void
  allowTouchMove: (value: boolean) => void
  isSwiperVisible: Ref<boolean>
  setSwiperVisibility: (isVisible: boolean) => void
  api: Api
  keyboardBindings: ReturnType<typeof useKeyboardBindings>
  slideDirection: ReturnType<typeof useSlideDirection>
  closeApp: () => void
  options?: Options
  cacheNavigation: ReturnType<typeof useCacheNavigation>
  nodeId?: string
  handleKeyAction: ActionCallback
  annotationLayer: ReturnType<typeof useAnnotationLayer>
  updateSlideInSwiper: (index: number) => void
  rootElement: Ref<HTMLElement | null>
  updateResolvedData: (resolvedData: CollectionPlayer) => void
}

export function provideAppStore({
  data,
  isEditMode,
  rootElement,
  api,
  emit,
  options,
  isLoading,
  nodeId,
  inModal,
}: StorePayload) {
  const swiperElement = ref<(HTMLElement & { swiper: Swiper }) | null>(null)
  const ignoreSlideChange = ref(false)
  const isOpenTableOfContents = ref(false)
  const isSwiperVisible = ref(false)
  const cacheNavigation = useCacheNavigation(nodeId ?? '')
  const annotationLayer = useAnnotationLayer()
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )

  const setSwiperVisibility = (isVisible: boolean) => (isSwiperVisible.value = isVisible)

  const getInitialActiveGroup = () => {
    if (nodeId) {
      const id = cacheNavigation.get(nodeId)

      if (id) {
        const group = visibleGroups.value.find((_slide) => _slide.id === id)

        if (group) {
          return group
        }
      }
    }

    return visibleGroups.value?.[0] ?? null
  }

  const getInitialActiveSlide = () => {
    if (activeGroup.value) {
      const cacheGroupId = cacheNavigation.createCacheId(activeGroup.value.id)
      const cachedSlideId = cacheNavigation.get(cacheGroupId)
      const slide = activeGroup.value?.slides.find((_slide) => _slide.id === cachedSlideId)

      if (slide) {
        return slide
      }
    }

    return activeGroup.value?.slides?.[0] ?? null
  }

  const toggleTableOfContents = () => {
    isOpenTableOfContents.value = !isOpenTableOfContents.value
  }

  const visibleGroups = computed(() => {
    if (isEditMode.value) {
      return data.value.groups
    }

    return data.value.groups.filter((group) => group.slides?.length)
  })

  const activeGroup = ref<CollectionGroup | null>(getInitialActiveGroup())
  const activeSlide = ref<CollectionSlide | null>(getInitialActiveSlide())
  const updateSlideInSwiper = (slideIndex: number) => swiperElement.value?.swiper.slideTo(slideIndex)

  const setActiveSlide = (group?: CollectionGroup, slide?: CollectionSlide) => {
    if (!group) {
      activeGroup.value = null
      activeSlide.value = null

      return
    } else if (activeGroup.value !== group) {
      activeGroup.value = group
    }

    if (!activeGroup.value.slides?.length) {
      activeSlide.value = null
      return
    }

    const cacheGroupId = cacheNavigation.createCacheId(group.id)
    const cachedSlide = cacheNavigation.get(cacheGroupId)

    if (!slide && cachedSlide) {
      slide = group?.slides.find((slide) => slide.id === cachedSlide)

      if (!slide) {
        cacheNavigation.remove(cacheGroupId)
      }
    }

    let slideIndex = slide ? activeGroup.value.slides.findIndex((_slide) => _slide.id === slide.id) : 0
    slideIndex = Math.max(slideIndex, 0)

    activeSlide.value = activeGroup.value.slides[slideIndex]
    ignoreSlideChange.value = true

    if (activeSlide.value) {
      if (nodeId) {
        cacheNavigation.set(nodeId, group.id)
      }

      cacheNavigation.set(cacheGroupId, activeSlide.value.id)
    }

    setTimeout(() => {
      updateSlideInSwiper(slideIndex)
      ignoreSlideChange.value = false
    }, 0)
  }

  const onSlideChange = (customEvent: CustomEvent<[swiper: Swiper]>) => {
    const details = customEvent.detail?.[0]

    if (!details) {
      return
    }

    const { activeIndex, previousIndex, slides } = details

    const previousSlide = slides?.[previousIndex]

    if (previousSlide) {
      pauseVideo(previousSlide)
    }

    if (ignoreSlideChange.value) {
      return
    }

    if (Number.isInteger(activeIndex)) {
      setActiveSlide(activeGroup.value ?? undefined, activeGroup.value?.slides?.[activeIndex] ?? undefined)
    }
  }

  const pauseVideo = (slide: HTMLElement) => {
    const video = slide.querySelector('video')

    video?.pause()
  }

  const activateNextGroup = () => {
    if (!activeGroup.value) {
      return
    }

    const activeGroupIndex = visibleGroups.value.findIndex((group) => group.id === activeGroup.value?.id)

    if (activeGroupIndex === -1 || activeGroupIndex >= visibleGroups.value.length - 1) {
      return
    }

    const nextGroup = visibleGroups.value[activeGroupIndex + 1]

    if (nextGroup) {
      setActiveSlide(nextGroup)
    }
  }

  const activatePreviousGroup = () => {
    if (!activeGroup.value) {
      return
    }

    const activeGroupIndex = visibleGroups.value.findIndex((group) => group.id === activeGroup.value?.id)

    if (activeGroupIndex <= 0) {
      return
    }

    const previousGroup = visibleGroups.value[activeGroupIndex - 1]

    if (previousGroup) {
      setActiveSlide(previousGroup)
    }
  }

  const fitToWidth = ref(false)
  const zoom = pitcherSettings.value?.use_collection_player_with_annotations
    ? useNewZoom(annotationLayer, activeSlide)
    : useZoom(swiperElement, activeSlide)

  const fullscreen = useFullscreen()

  const toggleFitToWidth = () => {
    if (pitcherSettings.value?.use_collection_player_with_annotations) {
      annotationLayer.scalingMode.value =
        annotationLayer.scalingMode.value === 'fit-width' ? 'fit-content' : 'fit-width'
    }

    fitToWidth.value = !fitToWidth.value

    zoom.resetZoom()
    rootElement.value?.focus()
  }

  const allowTouchMove = (value: boolean) => {
    if (!swiperElement.value) {
      return
    }

    if (pitcherSettings.value?.use_collection_player_with_annotations) {
      swiperElement.value.swiper.allowTouchMove =
        annotationLayer.activeTool.value && zoom.isZoomEnabled.value ? false : value
    } else {
      swiperElement.value.swiper.allowTouchMove = value
    }
  }

  const hasManyGroups = computed(() => visibleGroups.value.length > 1)
  const slideDirection = useSlideDirection(api)

  const handleKeyAction: ActionCallback = (key) => {
    const isDirectionHorizontal = slideDirection.direction.value === AxisDirection.HORIZONTAL

    if (key === 'escape') {
      fullscreen.exitFullscreen()
    } else if (key === 'left') {
      if (isDirectionHorizontal) {
        swiperElement.value?.swiper.slidePrev()
      } else {
        activatePreviousGroup()
      }
    } else if (key === 'right') {
      if (isDirectionHorizontal) {
        swiperElement.value?.swiper.slideNext()
      } else {
        activateNextGroup()
      }
    } else if (key === 'up') {
      if (isDirectionHorizontal) {
        activatePreviousGroup()
      } else {
        swiperElement.value?.swiper.slidePrev()
      }
    } else if (key === 'down') {
      if (isDirectionHorizontal) {
        activateNextGroup()
      } else {
        swiperElement.value?.swiper.slideNext()
      }
    }
  }

  const keyboardBindings = useKeyboardBindings(rootElement, handleKeyAction)

  const updateResolvedData = (resolvedData: CollectionPlayer) => {
    // Force reactivity by creating a new object reference with deep cloning
    data.value = {
      ...resolvedData,
      groups: resolvedData.groups.map((group) => ({
        ...group,
        slides: [...group.slides],
      })),
    }

    // Reset active states to ensure proper re-initialization with new data
    const newActiveGroup = data.value?.groups?.[0] ?? null
    const newActiveSlide = newActiveGroup?.slides?.[0] ?? null

    // Only update if different to avoid unnecessary re-renders
    if (activeGroup.value?.id !== newActiveGroup?.id) {
      activeGroup.value = newActiveGroup
    }
    if (activeSlide.value?.id !== newActiveSlide?.id) {
      activeSlide.value = newActiveSlide
    }
  }

  const storeData: StoreData = {
    isLoading,
    data,
    slideDirection,
    swiperElement,
    zoom,
    fullscreen,
    isOpenTableOfContents,
    isEditMode,
    visibleGroups,
    hasManyGroups,
    activeGroup,
    activeSlide,
    fitToWidth,
    isSwiperVisible,
    inModal,
    keyboardBindings,
    cacheNavigation,
    nodeId,
    setActiveSlide,
    onSlideChange,
    activatePreviousGroup,
    activateNextGroup,
    toggleTableOfContents,
    toggleFitToWidth,
    allowTouchMove,
    setSwiperVisibility,
    handleKeyAction,
    api,
    options,
    closeApp: () => emit('close'),
    annotationLayer,
    updateSlideInSwiper,
    rootElement,
    updateResolvedData,
  }

  provide<StoreData>(StoreKey, storeData)

  return storeData
}

export function useAppStore() {
  const storeData = inject<StoreData>(StoreKey)

  if (!storeData) {
    throw new Error('useAppStore() is called without provider.')
  }

  return storeData
}
